import { useState, useEffect } from 'react';
import { Check, AlertCircle, Wifi, MapPin, Hash, Tag, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

interface DeviceFormData {
  name: string;
  description: string;
  serial_number: string;
  mac_address: string;
  location_id: string;
}

interface ValidationErrors {
  [key: string]: string;
}

interface DeviceFormProps {
  initialData?: Partial<DeviceFormData>;
  locations: Array<{ id: string; name: string; description?: string }>;
  isLoading?: boolean;
  isEdit?: boolean;
  onSubmit: (data: DeviceFormData) => Promise<void>;
  onCancel: () => void;
}

export function DeviceForm({
  initialData = {},
  locations,
  isLoading = false,
  isEdit = false,
  onSubmit,
  onCancel,
}: DeviceFormProps) {
  const [formData, setFormData] = useState<DeviceFormData>({
    name: '',
    description: '',
    serial_number: '',
    mac_address: '',
    location_id: '',
    ...initialData,
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'serial_number':
        if (!value.trim()) return 'Serial number is required';
        if (value.length < 3) return 'Serial number must be at least 3 characters';
        if (!/^[A-Za-z0-9-_]+$/.test(value))
          return 'Serial number can only contain letters, numbers, hyphens, and underscores';
        return '';
      case 'name':
        if (!value.trim()) return 'Device name is required';
        if (value.length < 2) return 'Name must be at least 2 characters';
        return '';
      case 'mac_address':
        if (value && !/^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/.test(value)) {
          return 'Invalid MAC address format (e.g., AA:BB:CC:DD:EE:FF)';
        }
        return '';
      case 'location_id':
        if (!value) return 'Please select a location';
        return '';
      default:
        return '';
    }
  };

  const handleFieldChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Validate field if it has been touched
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleFieldBlur = (name: string) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name as keyof DeviceFormData]);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key as keyof DeviceFormData]);
      if (error) newErrors[key] = error;
    });
    setErrors(newErrors);
    setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      await onSubmit(formData);
    }
  };

  const getFieldIcon = (fieldName: string) => {
    switch (fieldName) {
      case 'serial_number':
        return <Hash className="h-4 w-4" />;
      case 'name':
        return <Tag className="h-4 w-4" />;
      case 'mac_address':
        return <Wifi className="h-4 w-4" />;
      case 'location_id':
        return <MapPin className="h-4 w-4" />;
      case 'description':
        return <FileText className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const selectedLocation = locations.find((loc) => loc.id === formData.location_id);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">{isEdit ? 'Update Device Information' : 'Add New Device'}</h3>
        <p className="text-sm text-muted-foreground">
          {isEdit
            ? 'Modify the device details below to update your IoT device configuration.'
            : 'Provide the basic information to register your new IoT device.'}
        </p>
      </div>

      <Separator />

      {/* Basic Information */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Tag className="h-4 w-4" />
            Basic Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Serial Number */}
          <div className="space-y-2">
            <Label htmlFor="serial_number" className="flex items-center gap-2">
              {getFieldIcon('serial_number')}
              Serial Number
              <Badge variant="destructive" className="text-xs">
                Required
              </Badge>
            </Label>
            <Input
              id="serial_number"
              value={formData.serial_number}
              onChange={(e) => handleFieldChange('serial_number', e.target.value)}
              onBlur={() => handleFieldBlur('serial_number')}
              disabled={isEdit || isLoading}
              placeholder="e.g., ESP32-001, SENSOR-ABC123"
              className={cn(
                'transition-all duration-200',
                errors.serial_number && touched.serial_number && 'border-destructive focus-visible:ring-destructive',
                !errors.serial_number && touched.serial_number && formData.serial_number && '',
              )}
            />
            {errors.serial_number && touched.serial_number && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                {errors.serial_number}
              </div>
            )}
            {isEdit && (
              <p className="text-xs text-muted-foreground">Serial number cannot be changed after device creation</p>
            )}
          </div>

          {/* Device Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-2">
              {getFieldIcon('name')}
              Device Name
              <Badge variant="destructive" className="text-xs">
                Required
              </Badge>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              onBlur={() => handleFieldBlur('name')}
              disabled={isLoading}
              placeholder="e.g., Temperature Sensor - Room 101"
              className={cn(
                'transition-all duration-200',
                errors.name && touched.name && 'border-destructive focus-visible:ring-destructive',
                !errors.name && touched.name && formData.name && '',
              )}
            />
            {errors.name && touched.name && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                {errors.name}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Network Configuration */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Wifi className="h-4 w-4" />
            Network Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* MAC Address */}
          <div className="space-y-2">
            <Label htmlFor="mac_address" className="flex items-center gap-2">
              {getFieldIcon('mac_address')}
              MAC Address
              <Badge variant="secondary" className="text-xs">
                Optional
              </Badge>
            </Label>
            <Input
              id="mac_address"
              value={formData.mac_address}
              onChange={(e) => handleFieldChange('mac_address', e.target.value.toUpperCase())}
              onBlur={() => handleFieldBlur('mac_address')}
              disabled={isLoading}
              placeholder="AA:BB:CC:DD:EE:FF"
              className={cn(
                'transition-all duration-200',
                errors.mac_address && touched.mac_address && 'border-destructive focus-visible:ring-destructive',
                !errors.mac_address && touched.mac_address && formData.mac_address && '',
              )}
            />
            {errors.mac_address && touched.mac_address && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                {errors.mac_address}
              </div>
            )}
            <p className="text-xs text-muted-foreground">Hardware MAC address for network identification</p>
          </div>
        </CardContent>
      </Card>

      {/* Location & Description */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Location & Description
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location_id" className="flex items-center gap-2">
              {getFieldIcon('location_id')}
              Location
              <Badge variant="destructive" className="text-xs">
                Required
              </Badge>
            </Label>
            <Select
              value={formData.location_id}
              onValueChange={(value) => handleFieldChange('location_id', value)}
              disabled={isLoading}
            >
              <SelectTrigger
                className={cn(
                  'transition-all duration-200',
                  errors.location_id && touched.location_id && 'border-destructive focus-visible:ring-destructive',
                  !errors.location_id && touched.location_id && formData.location_id && '',
                )}
                onBlur={() => handleFieldBlur('location_id')}
              >
                <SelectValue placeholder="Select a location for this device" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((location) => (
                  <SelectItem key={location.id} value={location.id}>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <div className="">
                        <div className="font-medium text-left">{location.name}</div>
                        {location.description && (
                          <div className="text-xs text-muted-foreground">{location.description}</div>
                        )}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.location_id && touched.location_id && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                {errors.location_id}
              </div>
            )}
            {/* {selectedLocation && (
              <div className="flex items-center gap-2 p-2 bg-muted rounded-md">
                <Check className="h-4 w-4 text-green-500" />
                <span className="text-sm">Selected: {selectedLocation.name}</span>
              </div>
            )} */}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="flex items-center gap-2">
              {getFieldIcon('description')}
              Description
              <Badge variant="secondary" className="text-xs">
                Optional
              </Badge>
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              onBlur={() => handleFieldBlur('description')}
              disabled={isLoading}
              placeholder="Describe the device purpose, capabilities, or any special notes..."
              rows={3}
              className="transition-all duration-200 resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Provide additional context about this device ({formData.description.length}/500 characters)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Info Alert */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {isEdit
            ? 'Changes will be applied immediately and may affect device connectivity.'
            : "After creating the device, you'll be able to add sensors and configure monitoring rules."}
        </AlertDescription>
      </Alert>

      {/* Action Buttons */}
      <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading} className="flex-1 sm:flex-none">
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isLoading || Object.keys(errors).some((key) => errors[key])}
          className="flex-1 sm:flex-none min-w-[120px]"
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isEdit ? 'Save Changes' : 'Create Device'}
        </Button>
      </div>
    </form>
  );
}
